import { CartUpdateEvent } from '@theme/events';

const root = window.Shopify.routes.root;
const marker = 'jr-gwp-v1';
function needed() {
  const node = document.querySelector('[data-jr-gwp-state]');
  if (!node) return false;
  const current = JSON.parse(node.textContent);
  return current.enabled || current.cart.items.some((item) => item.properties?._jr_gwp === marker);
}
let pending;
let dirty = false;
let blocked = '';
const owned = (cart) => cart.items.filter((item) => item.properties?._jr_gwp === marker);
const signature = (state) => JSON.stringify([state.title, state.giftId, state.quantity, state.cart.items.filter((item) => item.properties?._jr_gwp !== marker).map((item) => [item.key, item.quantity, item.final_price])]);
const free = (item, current) => current.freeGiftKeys.includes(item.key);

async function state() {
  const url = new URL(window.location.href);
  url.searchParams.delete('sections');
  url.searchParams.set('section_id', 'jr-gwp-state');
  url.searchParams.set('_gwp', String(Date.now()));
  const response = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(15000) });
  if (!response.ok) throw new Error('Gift eligibility unavailable');
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const node = html.querySelector('[data-jr-gwp-state]');
  if (!node) throw new Error('Gift settings unavailable');
  return JSON.parse(node.textContent);
}

async function mutate(endpoint, body) {
  const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((node) => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
  const response = await fetch(root + endpoint + '.js', {
    method: 'POST', signal: AbortSignal.timeout(15000), headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
  });
  const result = await response.json();
  if (!response.ok || result.errors) throw new Error('Gift cart update failed');
  return result;
}

async function removeGifts(cart) {
  const updates = Object.fromEntries(owned(cart).map((item) => [item.key, 0]));
  if (Object.keys(updates).length) await mutate('cart/update', { updates });
}

async function refresh() {
  const current = await state();
  document.dispatchEvent(new CartUpdateEvent(current.cart, marker, { source: marker, itemCount: current.cart.item_count }));
}

async function reconcile() {
  const current = await state();
  const gifts = owned(current.cart);
  const wanted = current.active && current.available && current.eligibleKeys.length > 0;
  if (!wanted) {
    if (gifts.length) { await removeGifts(current.cart); await refresh(); }
    blocked = '';
    return;
  }
  if (gifts.length === 1 && gifts[0].variant_id === current.giftId && gifts[0].quantity === current.quantity && free(gifts[0], current)) return;
  const key = signature(current);
  if (gifts.length) await removeGifts(current.cart);
  if (blocked === key) { if (gifts.length) await refresh(); return; }
  try {
    await mutate('cart/add', { items: [{ id: current.giftId, quantity: current.quantity, properties: { _jr_gwp: marker } }] });
    const checked = await state();
    const added = owned(checked.cart);
    if (!checked.active || !checked.eligibleKeys.length || added.length !== 1 || !free(added[0], checked)) {
      blocked = key;
      await removeGifts(checked.cart);
    }
  } catch (error) {
    blocked = key;
    const checked = await state();
    await removeGifts(checked.cart);
    throw error;
  } finally {
    await refresh();
  }
}

function sync() {
  if (!needed()) return Promise.resolve();
  dirty = true;
  if (!pending) {
    pending = (async () => {
      try {
        while (dirty) {
          dirty = false;
          await reconcile();
        }
      } finally { pending = undefined; }
    })();
  }
  return pending;
}

const schedule = () => sync().catch((error) => console.warn('Gift with purchase:', error.message));
document.addEventListener('cart:update', (event) => { if (event.detail?.sourceId !== marker) schedule(); });
document.addEventListener('discount:update', schedule);
document.addEventListener('shopify:section:load', schedule);
window.addEventListener('pageshow', schedule);
window.addEventListener('focus', schedule);
setInterval(() => { if (document.visibilityState === 'visible') schedule(); }, 60000);

let checkingOut = false;
async function checkout(event) {
  event.preventDefault();
  event.stopImmediatePropagation();
  if (checkingOut) return;
  checkingOut = true;
  document.documentElement.setAttribute('data-jr-gwp-busy', '');
  try {
    await sync();
    const checked = await state();
    if (owned(checked.cart).some((item) => !checked.active || !checked.eligibleKeys.length || !free(item, checked))) {
      await removeGifts(checked.cart);
      await refresh();
    }
    window.location.assign(root + 'checkout');
  } catch (error) {
    console.warn('Gift with purchase:', error.message);
    const message = document.querySelector('[data-jr-gwp-error]');
    if (message) { message.hidden = false; message.focus(); }
  } finally {
    checkingOut = false;
    document.documentElement.removeAttribute('data-jr-gwp-busy');
  }
}

document.addEventListener('click', (event) => {
  if (needed() && event.target.closest?.('[name="checkout"], a[href="/checkout"], a[href="' + root + 'checkout"]')) checkout(event);
}, true);
document.addEventListener('submit', (event) => {
  if (needed() && event.submitter?.name === 'checkout') checkout(event);
}, true);
schedule();

window.JRGiftWithPurchase = {
  async check() {
    const current = await state();
    const summary = {
      enabled: current.enabled,
      activeDateAndCurrency: current.active,
      giftVariantId: current.giftId,
      giftAvailable: current.available,
      qualifyingCartLines: current.eligibleKeys.length,
      automaticGiftLines: owned(current.cart).length,
      freeGiftLines: current.freeGiftKeys.length,
      expectedDiscountTitle: current.title,
      blockedAfterDiscountMismatch: blocked !== '',
    };
    console.table(summary);
    return summary;
  },
};
